import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { NgControl, NgModel } from '@angular/forms';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { FloorOption, FloorPickerHelper } from './helper/floor-picker-helper';
import { IPBoardFloorHelper } from './helper/ip-board-floor-helper';

@Component({
  selector: 'floor-picker',
  templateUrl: './floor-picker.component.html',
  styleUrls: ['./floor-picker.component.scss']
})
export class FloorPickerComponent extends SubscriptionBasedComponent
  implements OnInit, OnChanges {
  @Input()
  _value: string;
  @Input()
  placeholder: string = 'Verdieping';
  @Input()
  disabled: boolean = false;
  @Input()
  ipBoardPartForFloorFiltering: IPBoardPart;
  @Output()
  higherFloorOptionSelected = new EventEmitter<boolean>();

  @ViewChild('higherFloorInput', { static: true })
  higherFloorInput: ElementRef;

  onChange: any = GeneralHelper.noop;
  onTouch: any = GeneralHelper.noop;

  inputValue: string;

  private static FLOOR_OPTIONS_MAP = new Map<string, FloorOption>(
    FloorPickerHelper.FLOOR_OPTIONS.filter(o => o.value != null).map(o => [
      o.value,
      o
    ])
  );

  floorOptions = FloorPickerHelper.FLOOR_OPTIONS;
  selectedFloorOption: FloorOption;

  constructor(
    public ngControl: NgControl, // necessary in order to access initial value
    public projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
    this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    const inputValue = this.getInitialValue();
    const selectedFloorOption = FloorPickerComponent.FLOOR_OPTIONS_MAP.get(
      inputValue
    );
    this.selectedFloorOption =
      selectedFloorOption == null
        ? StringHelper.isEmpty(inputValue)
          ? null
          : FloorPickerHelper.FLOOR_OPTIONS[
              FloorPickerHelper.FLOOR_OPTIONS.length - 1
            ]
        : selectedFloorOption;
    if (
      this.selectedFloorOption != null &&
      this.selectedFloorOption.value == null
    ) {
      this.inputValue = inputValue;
    }

    if (this.ipBoardPartForFloorFiltering != null) {
      this.floorOptions = IPBoardFloorHelper.getFloorOptionsForIPBoardPart(
        this.ipBoardPartForFloorFiltering
      );
    }

    this.initializeIPBoardFloorFilterSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes['ipBoardPartForFloorFiltering']) {
      this.processIPBoardPartForFloorFilteringChange(
        changes['ipBoardPartForFloorFiltering'].currentValue
      );
    }
  }

  private initializeIPBoardFloorFilterSubscription(): void {
    this.addSubscription(
      this.projectDisplayObservableService.getIPBoardConfigurationChangedSubscribable(),
      res => {
        if (
          res != null &&
          res.ipBoardPart != null &&
          this.ipBoardPartForFloorFiltering != null &&
          (res.ipBoardPart === this.ipBoardPartForFloorFiltering ||
            res.ipBoardPart.id === this.ipBoardPartForFloorFiltering.id)
        ) {
          this.floorOptions = IPBoardFloorHelper.getFloorOptionsForIPBoardPart(
            this.ipBoardPartForFloorFiltering
          );
          if (
            this.selectedFloorOption != null &&
            !this.floorOptions.some(
              o => o.value === this.selectedFloorOption.value
            )
          ) {
            this.selectedFloorOption = null;
            this.value = null;
          }
        }
      }
    );
  }

  private processIPBoardPartForFloorFilteringChange(
    ipBoardPartForFloorFiltering: IPBoardPart
  ): void {
    this.floorOptions = IPBoardFloorHelper.getFloorOptionsForIPBoardPart(
      ipBoardPartForFloorFiltering
    );
    if (
      this.selectedFloorOption != null &&
      !this.floorOptions.some(o => o.value === this.selectedFloorOption.value)
    ) {
      this.selectedFloorOption = null;
      this.value = null;
    }
  }

  private getInitialValue(): string {
    return this.ngControl instanceof NgModel
      ? this.ngControl.model
      : this.ngControl.value;
  }

  get value() {
    return this._value;
  }

  set value(val: string) {
    this._value = val;

    this.onChange(val);
    this.onTouch(val);
  }

  writeValue(value: string): void {
    this._value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  onSelectionChanged(): void {
    const higherSelected =
      this.selectedFloorOption != null &&
      this.selectedFloorOption.value == null;
    this.value = higherSelected ? null : this.selectedFloorOption.value;
    this.inputValue = null;
    if (higherSelected) {
      setTimeout(() => this.higherFloorInput.nativeElement.select(), 0);
    }
    this.higherFloorOptionSelected.emit(higherSelected);
  }

  onInputValueChanged(): void {
    this.value = this.inputValue;
  }

  compareOptions(o1: FloorOption, o2: FloorOption): boolean {
    return (
      (o1 == null) == (o2 == null) &&
      ((o1 == null && o2 == null) || o1.label === o2.label)
    );
  }
}
